import React, { useMemo, useState } from 'react';

export interface AdultQuestion {
  a: number;
  b: number;
  options: number[];
}

/** A sum small children can't do yet (answers 8–18) with three choices. */
export function makeAdultQuestion(rand: () => number = Math.random): AdultQuestion {
  const a = 4 + Math.floor(rand() * 6);
  const b = 4 + Math.floor(rand() * 6);
  const answer = a + b;
  const set = new Set<number>([answer]);
  const deltas = [-3, -2, -1, 1, 2, 3];
  while (set.size < 3) {
    set.add(answer + deltas[Math.floor(rand() * deltas.length)]);
  }
  const options = Array.from(set).sort(() => rand() - 0.5);
  return { a, b, options };
}

export interface AdultCheckProps {
  onPass: () => void;
  onCancel: () => void;
}

/** Grown-ups only: answer a sum to continue. A wrong answer just gives a new sum. */
export const AdultCheck: React.FC<AdultCheckProps> = ({ onPass, onCancel }) => {
  const [seed, setSeed] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const q = useMemo(() => makeAdultQuestion(), [seed]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="For voksne"
      data-name="adult-check"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl text-center">
        <h2 className="font-display text-2xl font-bold text-gray-800 mb-1">For voksne</h2>
        <p className="font-body text-gray-600 mb-5">Svar for å åpne innstillingene.</p>
        <p className="font-display text-4xl font-bold text-gray-800 mb-5" data-name="adult-question">
          {q.a} + {q.b} = ?
        </p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {q.options.map((n) => (
            <button
              key={n}
              type="button"
              data-name={`adult-answer-${n}`}
              className="min-h-[72px] rounded-2xl bg-[#E8F4FA] font-display text-2xl font-bold text-gray-800 active:scale-95"
              onClick={() => (n === q.a + q.b ? onPass() : setSeed((s) => s + 1))}
            >
              {n}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[56px] w-full rounded-2xl bg-gray-100 font-display text-lg font-bold text-gray-600"
        >
          Avbryt
        </button>
      </div>
    </div>
  );
};

export default AdultCheck;
